/**
 * Tenant admin route definitions — workspace-level + agent-detail routes.
 *
 * Migrated off the legacy build-time `featureVisibility.ts` onto the shared
 * fail-closed Feature Gate (`web/src/feature-gate`): release-gated routes are
 * built with `buildGatedRoutes` so a disabled feature's `<Route>` is OMITTED
 * (and its chunk code-split out) rather than rendered-then-redirected. Keys are
 * the canonical dot-namespace shared with the backend route-gate and
 * `deploy/feature-toggles/feature-toggles.yaml`.
 *
 * The load-bearing `path:"*"` agentDetailRoute wildcard (the agent-detail shell,
 * NOT a catch-all) is preserved verbatim. A genuine `<NotFoundPage>` terminal is
 * added for truly-unknown workspace-level paths (SPEC-FEATURE-TOGGLES RULE-003/005).
 *
 * SURFACE FILTER (SPEC-TENANT-SURFACES RULE-006 (2), RULE-005(b)(c), RULE-011;
 * TASK-TSURF-W3-NAV-ROUTE-FILTER). Both builders take the RESOLVED surface set
 * (`useSurfaceSet()`, passed in by the router) and register a keyed route only
 * when its release gate is on AND its surface is usable by THIS caller; the
 * permission wrapper inside the element is unchanged. An unusable surface's
 * `<Route>` is OMITTED, so a deep link falls through to the not-found page
 * (the agent shell's `*` child, or the workspace `*` terminal). A nav group
 * with no usable child registers no redirect. Every page behind a surface is
 * `React.lazy` + `Suspense`, so an omitted surface's chunk is never loaded.
 * The builders derive no tier and hold no copy of the registry (RULE-003,
 * RULE-016): they only ask the set.
 *
 * @module apps/tenant/routes
 * @see TASK-TA-ROUTES-CONFIG
 * @see TASK-FT-MIGRATE-TENANT-ADMIN
 * @see SPEC-FEATURE-TOGGLES
 */
import React, { createContext, lazy, Suspense, useContext, useEffect } from "react";
import { matchRoutes, Navigate, Outlet, useLocation, useParams, type Location, type RouteObject } from "react-router-dom";
import { CircularProgress, Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { agentPostureQueryOptions, tenantAgentOptionsQueryOptions } from "../../api/agentPosture";
import { ProtectedRoute } from "../../components/shared/ProtectedRoute";
import { PermissionGate } from "../../components/shared/PermissionGate";
import { RouteErrorBoundary } from "../../components/shared/RouteErrorBoundary";
import { buildGatedRoutes, isEnabled, NotFoundPage } from "../../feature-gate";
import { resolvePluginComponent } from "../../plugins/registry";
import { usePermissionContext } from "../../store/PermissionContext";
import {
  agentStartKey,
  firstUsableAgentChild,
  firstUsableWorkspaceChild,
  resolveTenantLanding,
  type AgentLandingGroup,
  type WorkspaceLandingGroup,
} from "./landing";
import { useAgentStart, useLandingPredicate } from "./useLandingPredicate";
import { useAgentsMenuTarget } from "./useAgentsMenuTarget";
import type { SurfaceKey } from "../../surfaces/keys";
import {
  clearSurfaceRefusal,
  usePagePresence,
  useSurfaceRefusal,
  useSurfaceSet,
  type SurfaceSet,
} from "../../surfaces";

// Agent detail shell (reused from workspace — renders AgentDetailSidebar + Outlet)
const AgentDetailPage = lazy(() => import("../../pages/workspace/AgentDetailPage"));

// Agent config sub-pages
const AgentSettingsGeneral = lazy(() => import("../../pages/tenant/agent/AgentSettingsGeneral"));
const AgentSettingsKnowledge = lazy(() => import("../../pages/tenant/agent/AgentSettingsKnowledge"));
const AgentSettingsSecurity = lazy(() => import("../../pages/tenant/agent/AgentSettingsSecurity"));
const AgentSettingsAI = lazy(() => import("../../pages/tenant/agent/AgentSettingsAI"));
const AgentSettingsNotifications = lazy(() => import("../../pages/tenant/agent/AgentSettingsNotifications"));
const AgentSettingsIntegrations = lazy(() => import("../../pages/tenant/agent/AgentSettingsIntegrations"));
const AgentSettingsSupportChannels = lazy(() => import("../../pages/tenant/agent/AgentSettingsSupportChannels"));
const AgentSourcesFiles = lazy(() => import("../../pages/tenant/agent/SourcesFiles"));
// The merged data-sources page (SPEC-FRONTEND-RULE-066). Served at `sources`
// when agent.sources.unified is released; the two superseded list paths then
// redirect to it. Lazy like every other page, so the OFF arm never loads it.
// Where the read-only sources tree is released (agent.sources.tree) AND switched on
// for the town (surface agent.sources.index.tree), the page itself offers the tree
// in place of its table - D-STV-01, D-UST-01..03.
const SourcesIndexPage = lazy(() => import("../../pages/tenant/agent/DataSourcesPage"));
// Website (crawler) source-config pages — the modern crawler-framework plugin
// pages, routed as CORE routes (direct lazy import, NOT via the plugin registry)
// under the agent-detail shell. Replaces the frozen-legacy AgentSourcesWebsite.
// See the sources/website{,/create,/:sourceId} routes below.
const CrawlSourcesList = lazy(() => import("../../plugins/web-crawler/pages/SourcesWebsite"));
const CrawlSourceCreate = lazy(() => import("../../plugins/web-crawler/pages/SourcesWebsiteCreate"));
const CrawlSourceDetail = lazy(() => import("../../plugins/web-crawler/pages/CrawlSourceDetail"));
const AgentSourcesSchemas = lazy(() => import("../../pages/tenant/agent/SourcesMetadataSchemas"));
// Named after the file it renders (Playground.tsx, the plugin-registry shell).
// The old alias "AgentPlaygroundChat" shadowed a dead file of that exact name —
// the [[spec-target-live-route]] alias trap (deleted 2026-08-19).
const AgentPlaygroundPage = lazy(() => import("../../pages/tenant/agent/Playground"));

const AgentDeploy = lazy(() => import("../../pages/tenant/agent/AgentDeploy"));
const DeployJSWidget = lazy(() => import("../../pages/tenant/agent/deploy/JavaScriptWidgetSetup"));
const DeployWorkspace = lazy(() => import("../../pages/tenant/agent/deploy/WorkspaceDeploySetup"));
const DeployAPI = lazy(() => import("../../pages/tenant/agent/deploy/APISetup"));
const DeployPermalink = lazy(() => import("../../pages/tenant/agent/deploy/PermalinkSetup"));
const AgentReports = lazy(() => import("../../pages/tenant/agent/AgentReports"));
const AgentTraining = lazy(() => import("../../pages/tenant/agent/Training"));
const DocumentManagerPage = lazy(() => import("../../pages/tenant/agent/DocumentManagerPage"));
const KnowledgeBasePage = lazy(() => import("../../pages/tenant/agent/KnowledgeBasePage"));
const AgentSchedules = lazy(() => import("../../pages/tenant/agent/Schedules"));
const AgentJobHistory = lazy(() => import("../../pages/tenant/agent/JobHistory"));
const AgentAnalytics = lazy(() => import("../../pages/tenant/agent/AgentAnalytics"));
const AgentInsights = lazy(() => import("../../pages/tenant/agent/AgentInsights"));
const AgentChatLogs = lazy(() => import("../../pages/tenant/agent/ChatLogs"));
// Named after the file it renders (ABTesting.tsx). The old alias
// "AgentABPlayground" shadowed a dead file of that exact name (deleted 2026-08-19).
const AgentABTestingPage = lazy(() => import("../../pages/tenant/agent/ABTesting"));

const ApplicationsPage = lazy(() => import("../../pages/tenant/ApplicationsPage"));

// Workspace-level pages
const AgentsPage = lazy(() => import("../../pages/tenant/AgentsPage"));
const SourcesPage = lazy(() => import("../../pages/tenant/SourcesPage"));
const UsagePage = lazy(() => import("../../pages/tenant/UsagePage"));
const FeaturesPage = lazy(() => import("../../pages/tenant/FeaturesPage"));
// The user's OWN settings home (D-TSG-02, SPEC-TENANT-SURFACES UI-003). It is
// USER-level, deliberately not part of the workspace-scoped Settings group
// below, and it carries no ProtectedRoute: requiring workspace.settings.manage
// would put operator, user and viewer on Access Denied - three of the five
// roles, and exactly the ones the per-user tier grant exists to serve.
const MySettingsPage = lazy(() => import("../../pages/tenant/MySettingsPage"));

// Workspace settings pages
const SettingsGeneral = lazy(() => import("../../pages/tenant/settings/GeneralPage"));
const SettingsMembers = lazy(() => import("../../pages/tenant/settings/MembersPage"));
const SettingsRoles = lazy(() => import("../../pages/tenant/settings/RolesPage"));
const SettingsPermissions = lazy(() => import("../../pages/tenant/settings/PermissionsPage"));
const SettingsPlugins = lazy(() => import("../../pages/tenant/settings/PluginsPage"));
// SPEC-FRONTEND-UI-058, TASK-AIMARK-TENANT-PROVENANCE-SETTINGS-UI.
const SettingsAnswerProvenance = lazy(() => import("../../pages/tenant/settings/AnswerProvenancePage"));
const SettingsSchemas = lazy(() => import("../../pages/tenant/settings/SchemasPage"));
const PermissionsManagementPage = lazy(() => import("../../pages/tenant/settings/PermissionsManagementPage"));

// ---------------------------------------------------------------------------
// Loading fallback
// ---------------------------------------------------------------------------

function PageLoader(): React.ReactElement {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
      <CircularProgress />
    </Box>
  );
}

function withSuspense(Component: React.LazyExoticComponent<React.ComponentType>): React.ReactElement {
  return (
    <RouteErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Component />
      </Suspense>
    </RouteErrorBoundary>
  );
}

/** Wrap a lazy component in Suspense, a PermissionGate, and a RouteErrorBoundary. */
function withPermission(
  Component: React.LazyExoticComponent<React.ComponentType>,
  permission: string,
): React.ReactElement {
  return (
    <RouteErrorBoundary>
      <PermissionGate permission={permission}>
        <Suspense fallback={<PageLoader />}>
          <Component />
        </Suspense>
      </PermissionGate>
    </RouteErrorBoundary>
  );
}

function PluginRoute({ plugin, component, fallback }: {
  plugin: string; component: string; fallback?: React.ReactElement;
}): React.ReactElement {
  const C = resolvePluginComponent(plugin, component);
  // ".." is a relative climb to the parent route, not a surface target, so it
  // does not go through the landing helper (SPEC-TENANT-SURFACES-RULE-005(b)).
  if (!C) return fallback ?? <Navigate to=".." />;
  return <C />;
}

type LandingRedirectProps =
  | { scope: "agent"; group: AgentLandingGroup }
  | { scope: "workspace"; group: WorkspaceLandingGroup };

// `useLandingPredicate` - the predicate every landing in this file resolves
// through - lives in ./useLandingPredicate, so the shell header's logo resolves
// through the SAME assembly rather than a copy (TASK-TSURF-LOGO-TO-CHOOSER).

/**
 * The ONLY way this file redirects to a surface (SPEC-TENANT-SURFACES-RULE-005(b)):
 * ask the landing helper for the first child THIS caller can use and navigate
 * there. When no child is usable there is no redirect at all; the not-found
 * page renders instead of an empty content pane (RULE-005(d)) or an Access
 * Denied panel (the lockout the rule forbids). While permissions load, the
 * loader shows, so an admin is never bounced past settings before they resolve.
 *
 * THE WORKSPACE INDEX additionally runs the tenant landing chain
 * (EPIC-TENANT-SURFACES-AC-022 as amended by D-375,
 * TASK-TSURF-W3-LANDING-RESOLVER-CHG-002): where opening the product puts this
 * caller is the posture's question, not this file's. The posture read is
 * `agentPostureQueryOptions`, the ONE fetcher on `["me","agent-posture"]`
 * (SPEC-FRONTEND-RULE-077, QUERY-KEY-GATE), and the agent list is the landing
 * picker's own `["agents","landing-options"]` - both shared cache entries, and
 * both `enabled` ONLY for that one mount, so a group redirect (`/sources`,
 * `/settings`, the agent shell) starts neither read itself. (They are no longer
 * index-only reads, though: since the single-agent menu entry, the sidebar's
 * `useAgentsMenuTarget` keeps both subscribed on every workspace page, and the
 * agents page reads the same hook for its title - shared cache entries, so no
 * second fetcher, but one posture read per layout mount and focus refetch.)
 * Neither may decide
 * anything while it is in flight: a redirect is irreversible for the caller,
 * so the loader holds until both have settled, and a failed read (the 503 the
 * per-user leg answers with included) reaches the resolver as `null`, which
 * fails closed to the chooser. When the chain answers `null` - not even the
 * chooser is usable - the W0 group resolution below answers, exactly as it did
 * before D-375, and ends in no redirect at all.
 */
function LandingRedirect(props: LandingRedirectProps): React.ReactElement {
  const { loading } = usePermissionContext();
  const usable = useLandingPredicate();
  // D-TSG-32: the platform admin's agent start page goes FIRST in the agent
  // index (and in every chain that lands inside an agent); null = default order.
  const agentStart = useAgentStart();
  const { agentId } = useParams<{ agentId: string }>();
  // D-TSG-23 point 3: while the switch that decides a whole advanced page is
  // still being read, the landing waits rather than choosing without it.
  const { pending: switchPending } = usePagePresence();
  // The tenant landing chain belongs to the workspace INDEX alone.
  const chainMount = props.scope === "workspace" && props.group === "index";
  const posture = useQuery({ ...agentPostureQueryOptions(), enabled: chainMount });
  const agents = useQuery({ ...tenantAgentOptionsQueryOptions(), enabled: chainMount });
  if (loading || switchPending) return <PageLoader />;
  if (chainMount) {
    if (posture.isPending || agents.isPending) return <PageLoader />;
    // ONLY a successful read decides. `isSuccess`, not `data`, because a query
    // that failed after an earlier success still holds the old answer, and a
    // 503 from the per-user landing leg must fail closed rather than land the
    // caller inside an agent on the strength of a stale posture.
    const landing = resolveTenantLanding({
      posture: posture.isSuccess ? posture.data : null,
      agents: agents.isSuccess ? agents.data : null,
      usable,
      agentStart,
    });
    if (landing !== null) return <Navigate to={landing} replace />;
  }
  const target =
    props.scope === "agent"
      ? firstUsableAgentChild({ group: props.group, usable, startKey: agentStartKey(agentStart, agentId) })
      : firstUsableWorkspaceChild({ group: props.group, usable });
  if (target === null) return <NotFoundPage homePath="/" />;
  return <Navigate to={target} replace />;
}

/**
 * The agent chooser route (`/agents`).
 *
 * The owner, 2026-10-05: a caller with exactly one agent who may not create
 * another goes straight into that agent from the agents menu entry. The
 * sidebar entry already points INSIDE the agent (no visit of this route, so no
 * flash); this element covers every OTHER way in - a typed URL, the not-found
 * page's home button inside the agent shell (`homePath="/agents"`), an
 * in-product `navigate('/agents')` - by REPLACING the history entry, so Back
 * from inside the agent never lands on a route that bounces forward again.
 *
 * That includes the LOGIN landing: when the workspace index's
 * `LandingRedirect` resolves to the chooser (a `chooser` landing, the tenant
 * default or the caller's own per-user row), it lands here and is replaced in
 * turn, so such a caller now starts inside the agent - a change to the effect
 * of EPIC-TENANT-SURFACES-AC-022's chooser leg, read from the owner's
 * "mindenképpen", not a change to `resolveTenantLanding`.
 *
 * The target is `useAgentsMenuTarget().chooserRedirect`, the same assembly the
 * sidebar entry reads (landing.ts `resolveChooserRedirect`), so the route and
 * the entry agree by construction. It is `null` - render the chooser, exactly
 * as before - while a read is in flight or has failed, when the caller may
 * create, when there are zero or several agents, when the one agent has no
 * usable child surface, and for a caller who can manage the tenant's members,
 * whose header logo leads HERE as their home screen (D-SCA-10).
 *
 * The chooser page arrives as `children`, so the route table still names the
 * page it mounts (the route-tree snapshot reads it from the element chain).
 */
function AgentsChooserRoute({ children }: { children: React.ReactElement }): React.ReactElement {
  const { chooserRedirect } = useAgentsMenuTarget();
  if (chooserRedirect !== null) return <Navigate to={chooserRedirect} replace />;
  return children;
}

// ---------------------------------------------------------------------------
// Agent detail child routes
// ---------------------------------------------------------------------------
//
// Routes that carry a canonical release-toggle key (and were therefore gated by
// the legacy featureVisibility.ts) are filtered through buildGatedRoutes via the
// `gated()` helper below so a disabled toggle OMITS the <Route> entirely. Routes
// that the legacy config left permanently visible (no `false` rule) carry NO
// feature key and stay always-on.

/**
 * The not-found terminal is a TRANSIENT state, which RULE-017 says is NOT a
 * surface and MUST NOT get a registry row - so its `surface` is declared, but
 * declared as "none". Only a `path: "*"` route may carry it (asserted by
 * __tests__/surfaceKeys.test.ts).
 */
const NOT_A_SURFACE = null;

/**
 * The WORKSPACE index redirect has no registry row yet. Its agent-shell twin
 * does (`agent.shell.index`, kind page, configurable false); the workspace
 * index has no `tenant.*` equivalent in the registry, and the registry is not
 * this TASK's to change (TASK-TSURF-W1-NAV-ROUTE-KEYS). Declared explicitly so
 * the gap stays visible instead of being papered over with a borrowed key.
 */
const NO_REGISTRY_ROW = null;

/** A route entry of the tenant builders, with its registry key and release gate. */
interface KeyedRoute {
  route: RouteObject;
  /**
   * The registry surface key (SPEC-TENANT-SURFACES-RULE-016/017), REQUIRED on
   * every entry. A redirect carries the key of the MOUNT it occupies (the nav
   * group whose path it is, or `agent.shell.index` for the agent index), because
   * RULE-005(b) resolves it over that group's children. `null` only through
   * NOT_A_SURFACE / NO_REGISTRY_ROW above; such a route is not surface-gated.
   */
  surface: SurfaceKey | null;
  /** Canonical release-toggle key; when set the route is omitted if its toggle is off. */
  feature?: string;
}

/**
 * What the route builders read of the resolved surface set: the SAME object
 * `useSurfaceSet()` returns (ARCH-005-MOD-016's read path), narrowed to the
 * three fields the filter needs, so a test can hand in a set without React.
 */
export type RouteSurfaceFilter = Pick<SurfaceSet, "status" | "surfaces" | "isUsable">;

/**
 * Does the resolved set list a usable DESTINATION child of `group` - a row of
 * kind nav, page or nav_group whose parent is `group`? A `section` row is a
 * block inside a page, not somewhere to land (the registry parents two
 * sections, agent.sources.file-preview.rescan and
 * agent.sources.upload.metadata-dialog, directly on the agent.sources group),
 * so it never keeps a group alive.
 */
function hasUsableChild(filter: RouteSurfaceFilter, group: SurfaceKey): boolean {
  return Object.entries(filter.surfaces).some(
    ([key, row]) => row.parent === group && row.kind !== "section" && filter.isUsable(key),
  );
}

/**
 * RULE-006 (2) for one route: a keyed route is registered only when its
 * surface is usable by this caller; a route of a `nav_group` (the group's
 * landing redirect) additionally needs at least one usable child, otherwise
 * it registers nothing (RULE-005(b)(c)). Every row fact is read from the
 * resolved set - on the error path there are no rows, so only the keys the
 * server pinned (D-TSG-09) pass. `null` (not a surface / no registry row) is
 * not surface-gated.
 */
function surfaceRouteUsable(filter: RouteSurfaceFilter, key: SurfaceKey | null): boolean {
  if (key === null) return true;
  if (!filter.isUsable(key)) return false;
  if (filter.surfaces[key]?.kind === "nav_group") return hasUsableChild(filter, key);
  return true;
}

/**
 * Filter a list of (route, optional feature-key) pairs through BOTH gates: a
 * keyed route survives only when its release toggle is enabled (the shared
 * fail-closed release gate, unchanged) AND its surface is usable by this
 * caller (RULE-006 (2)). Each kept route carries its surface key in
 * `handle.surface`.
 */
function gateRoutes(entries: KeyedRoute[], surfaces: RouteSurfaceFilter): RouteObject[] {
  const out: RouteObject[] = [];
  for (const e of entries) {
    if (e.feature !== undefined) {
      // buildGatedRoutes resolves the descriptor against the live enabled set;
      // an empty result means the toggle is off -> the route is omitted.
      const kept = buildGatedRoutes([
        { path: "", feature: e.feature, loader: async () => ({ default: () => null }) },
      ]);
      if (kept.length === 0) continue;
    }
    if (!surfaceRouteUsable(surfaces, e.surface)) continue;
    out.push({ ...e.route, handle: { surface: e.surface } });
  }
  return out;
}

/**
 * The route table the tenant router mounted, so {@link SurfaceOutlet} can tell
 * which registry surface the CURRENT location belongs to. TenantRouter
 * (App.tsx) provides the same array it hands to `useRoutes`.
 */
export const SurfaceRouteTable = createContext<RouteObject[] | null>(null);

/**
 * The registry surface of the deepest matched route that carries one - the
 * `handle.surface` gateRoutes and buildAgentDetailRoute set. Null when nothing
 * matches or no matched route is a surface (NOT_A_SURFACE, NO_REGISTRY_ROW).
 */
export function currentRouteSurface(routes: RouteObject[] | null, location: Location): SurfaceKey | null {
  if (routes === null) return null;
  const matches = matchRoutes(routes, location) ?? [];
  for (let i = matches.length - 1; i >= 0; i--) {
    const surface = (matches[i].route.handle as { surface?: SurfaceKey | null } | undefined)?.surface;
    if (surface) return surface;
  }
  return null;
}

/**
 * The routed region of the two tenant shell layouts (TenantLayout,
 * AgentLayout). Three states, all fail closed:
 * - while the FIRST surfaces read is in flight no route is usable yet, so it
 *   shows the loader rather than a not-found the answer is about to replace;
 * - after a 403 `surface_not_available` it renders the SAME not-found page an
 *   unregistered route renders (RULE-015, SPEC-FRONTEND RULE-094 clause 4) -
 *   never an error dialog - but ONLY when the current route's own surface is
 *   not usable in the latest answer - the refetch the refusal triggered. A
 *   refusal of some OTHER surface's route (a page's optional read of a
 *   sibling's data) leaves the page mounted, and the page handles its failed
 *   call itself. Usually the refetched answer has already dropped the route
 *   from the table and the router renders the unregistered state itself; this
 *   check covers a table not yet rebuilt. The signal is cleared on the next
 *   navigation;
 * - otherwise the matched route (`<Outlet />`), including on the error path,
 *   where the pinned surfaces still route (RULE-014, SCEN-010).
 */
export function SurfaceOutlet({ homePath }: { homePath: string }): React.ReactElement {
  const { status, isUsable } = useSurfaceSet();
  const refusal = useSurfaceRefusal();
  const routes = useContext(SurfaceRouteTable);
  const location = useLocation();
  const { pathname } = location;
  useEffect(() => {
    clearSurfaceRefusal();
  }, [pathname]);
  if (status === "loading") return <PageLoader />;
  if (refusal !== null) {
    const surface = currentRouteSurface(routes, location);
    if (surface !== null && !isUsable(surface)) return <NotFoundPage homePath={homePath} />;
  }
  return <Outlet />;
}

// Built at RENDER time (under <ReleaseFeaturesGate>, post-bootstrap) so a route
// gated `true` in config actually surfaces once released (RULE-007), not frozen
// at module-import when the enabled set is still empty.
function buildAgentDetailChildren(surfaces: RouteSurfaceFilter): RouteObject[] {
  return gateRoutes([
  // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: a REDIRECT (the permanently-on category this rule's own message names); the same pre-existing redirect route, only its element changed from a literal <Navigate> to LandingRedirect (SPEC-TENANT-SURFACES-RULE-005(b)); every target it resolves to keeps its own gate.
  { surface: "agent.shell.index", route: { index: true, element: <LandingRedirect scope="agent" group="index" /> } },
  // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: a REDIRECT (the permanently-on category this rule's own message names); the same pre-existing redirect route, only its element changed from a literal <Navigate> to LandingRedirect (SPEC-TENANT-SURFACES-RULE-005(b)); every target it resolves to keeps its own gate.
  { surface: "agent.settings", route: { path: "settings", element: <LandingRedirect scope="agent" group="settings" /> } },
  // Agent settings — require workspace.settings.manage (always visible)
  { surface: "agent.settings.general", route: { path: "settings/general", element: withPermission(AgentSettingsGeneral, "workspace.settings.manage") } },
  { surface: "agent.settings.task", route: { path: "settings/task", element:
    <RouteErrorBoundary>
      <PermissionGate permission="workspace.settings.manage">
        <Suspense fallback={<PageLoader />}>
          <PluginRoute plugin="doc-processor" component="settings:processor" />
        </Suspense>
      </PermissionGate>
    </RouteErrorBoundary> } },
  { surface: "agent.settings.knowledge", route: { path: "settings/knowledge", element: withPermission(AgentSettingsKnowledge, "workspace.settings.manage") } },
  { surface: "agent.settings.security", route: { path: "settings/security", element: withPermission(AgentSettingsSecurity, "workspace.settings.manage") } },
  { surface: "agent.settings.ai", route: { path: "settings/ai", element: withPermission(AgentSettingsAI, "workspace.settings.manage") } },
  // Parked no-op settings pages — release-gated OFF (D4, W5 build-vs-delete pending)
  // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: release-gated via the outer `feature:` key (agent.settings.notifications) that buildGatedRoutes reads; the lint rule inspects the inner route object and can’t see the sibling key. Owner: boundary-contract program. Expires: 2026-08-20. Ref: DECISIONS W5.
  { surface: "agent.settings.notifications", route: { path: "settings/notifications", element: withPermission(AgentSettingsNotifications, "workspace.settings.manage") }, feature: "agent.settings.notifications" },
  // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: release-gated via the outer `feature:` key (agent.settings.integrations). Owner: boundary-contract program. Expires: 2026-08-20. Ref: DECISIONS W5.
  { surface: "agent.settings.integrations", route: { path: "settings/integrations", element: withPermission(AgentSettingsIntegrations, "workspace.settings.manage") }, feature: "agent.settings.integrations" },
  // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: release-gated via the outer `feature:` key (agent.settings.support-channels). Owner: boundary-contract program. Expires: 2026-08-20. Ref: DECISIONS W5.
  { surface: "agent.settings.support-channels", route: { path: "settings/support-channels", element: withPermission(AgentSettingsSupportChannels, "workspace.settings.manage") }, feature: "agent.settings.support-channels" },
  // Deploy — require workspace.agents.manage (always visible)
  { surface: "agent.deploy", route: { path: "deploy", element: withPermission(AgentDeploy, "workspace.agents.manage") } },
  { surface: "agent.deploy.js-widget", route: { path: "deploy/js-widget", element: withPermission(DeployJSWidget, "workspace.agents.manage") } },
  { surface: "agent.deploy.workspace", route: { path: "deploy/workspace", element: withPermission(DeployWorkspace, "workspace.agents.manage") } },
  { surface: "agent.deploy.api", route: { path: "deploy/api", element: withPermission(DeployAPI, "workspace.agents.manage") } },
  // Permalink (SPEC-FRONTEND-RULE-098): the page permission of its three
  // siblings; the WRITE capability is the server's can_manage_channels shown on
  // the page. Surface key (permanent) AND release key (short-lived) - both
  // obligations of SPEC-FEATURE-TOGGLES-RULE-008.
  { surface: "agent.deploy.permalink", route: { path: "deploy/permalink", element: withPermission(DeployPermalink, "workspace.agents.manage") }, feature: "agent.deploy.permalink" },
  // Playground — single route, renders via plugin registry resolution
  { surface: "agent.playground", route: { path: "playground", element: withSuspense(AgentPlaygroundPage) } },
  // A/B testing — release-gated (legacy agent:ab-testing -> agent.ab-testing)
  { surface: "agent.ab-testing", route: { path: "ab-testing", element: withSuspense(AgentABTestingPage) }, feature: "agent.ab-testing" },
  { surface: "agent.chat-logs", route: { path: "chat-logs", element: withSuspense(AgentChatLogs) } },
  { surface: "agent.report-history", route: { path: "report-history", element: withSuspense(AgentReports) } },
  // Documents — orphan DocumentManagerPage, release-gated OFF (D5, D-022-backed;
  // W5 build-vs-delete pending). Require workspace.agents.manage when released.
  // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: release-gated via the outer `feature:` key (agent.documents). Owner: boundary-contract program. Expires: 2026-08-20. Ref: DECISIONS W5.
  { surface: "agent.documents", route: { path: "documents", element: withPermission(DocumentManagerPage, "workspace.agents.manage") }, feature: "agent.documents" },
  // Knowledge base — require workspace.agents.manage
  { surface: "agent.knowledge", route: { path: "knowledge", element: withPermission(KnowledgeBasePage, "workspace.agents.manage") } },
  // Sources — require workspace.sources.manage.
  //
  // Two rollout arms, both live at once (RULE-066 clauses 1 + 6); TASK-ADS-RETIRE
  // deletes the legacy one. `agent.sources.unified` is read through the shared
  // fail-closed store selector rather than a `feature:` key because this is a
  // SWAP, not an omission: gateRoutes can only DROP a route, and the OFF arm has
  // to keep serving today's two pages. isEnabled() returns false whenever the set
  // is unresolved or the key is unknown, so the toggle is fail-closed exactly as
  // SPEC-FEATURE-TOGGLES RULE-005/006 requires, and this function is called at
  // RENDER time under <ReleaseFeaturesGate> (see buildAgentDetailChildren's own
  // note), so it reads the resolved set rather than an empty one (RULE-007).
  ...(isEnabled("agent.sources.unified")
    ? [
        // ON: one merged list at `sources`, and the two superseded list paths
        // REDIRECT to it. RULE-066 clause 6 — an in-product link, a browser
        // bookmark, or a link already sent by email must still land, never 404.
        // `relative="path"` is load-bearing: these are FLAT sibling routes, so a
        // route-relative ".." would climb to the agent shell, not to `sources`.
        // The ".." climbs are not surface targets, so they stay literal rather
        // than going through LandingRedirect (SPEC-TENANT-SURFACES-RULE-005(b)).
        // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: release-gated by the enclosing isEnabled("agent.sources.unified") ternary — this whole arm is absent when the toggle is off, which is stronger than a `feature:` key, not weaker. Same sibling-key blind spot the four markers below document.
        { surface: "agent.sources.index", route: { path: "sources", element: withPermission(SourcesIndexPage, "workspace.sources.manage") } },
        // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: a REDIRECT, the case this rule's own message names as permanently-on by design; it exists precisely so the superseded path never 404s (RULE-066 clause 6).
        { surface: "agent.sources.files", route: { path: "sources/files", element: <Navigate to=".." replace relative="path" /> } },
        // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: a REDIRECT, as above — deliberately NOT gated on agent.sources.website, because a bookmark to the old crawler list must land on the merged page even where the website half is unreleased (clause 5 renders it files-only).
        { surface: "agent.sources.website", route: { path: "sources/website", element: <Navigate to=".." replace relative="path" /> } },
      ]
    : [
        // OFF: today's routes, byte-for-byte unchanged.
        // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: a REDIRECT (the permanently-on category this rule's own message names); the same pre-existing redirect route, only its element changed from a literal <Navigate> to LandingRedirect (SPEC-TENANT-SURFACES-RULE-005(b)); every target it resolves to keeps its own gate.
        { surface: "agent.sources", route: { path: "sources", element: <LandingRedirect scope="agent" group="sources" /> } },
        { surface: "agent.sources.files", route: { path: "sources/files", element: withPermission(AgentSourcesFiles, "workspace.sources.manage") } },
        // Website (crawler) sources — release-gated (legacy agent:sources/website ->
        // agent.sources.website). The modern crawler-framework plugin pages, routed as
        // CORE routes (direct lazy import above) under the agent-detail shell: list ->
        // create -> detail. The list "Add" nav goes to `create`; a row/View goes to
        // `:sourceId`; both create + detail back-nav return to the list.
        // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: release-gated via the outer `feature:` key (agent.sources.website) that buildGatedRoutes reads; the lint rule inspects the inner route object and can't see the sibling key.
        { surface: "agent.sources.website", route: { path: "sources/website", element: withPermission(CrawlSourcesList, "workspace.sources.manage") }, feature: "agent.sources.website" },
      ]),
  // In BOTH arms, unchanged: the merged page navigates INTO create and detail
  // rather than replacing them, so they stay routed under their own
  // agent.sources.website gate (RULE-066 clause 6).
  // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: release-gated via the outer `feature:` key (agent.sources.website).
  { surface: "agent.sources.website.create", route: { path: "sources/website/create", element: withPermission(CrawlSourceCreate, "workspace.sources.manage") }, feature: "agent.sources.website" },
  // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: release-gated via the outer `feature:` key (agent.sources.website).
  { surface: "agent.sources.website.detail", route: { path: "sources/website/:sourceId", element: withPermission(CrawlSourceDetail, "workspace.sources.manage") }, feature: "agent.sources.website" },
  // Metadata Schemas — release-gated OFF behind agent.metadata (2026-08-25,
  // owner-directed). The document-metadata feature is non-functional end to end:
  // the document-service upload route discards schema_id/metadata_values, and
  // ARTIS never receives document metadata at all. A gated route is OMITTED, so
  // a deep link falls through to NotFoundPage.
  // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: release-gated via the outer `feature:` key (agent.metadata).
  { surface: "agent.sources.schemas", route: { path: "sources/schemas", element: withPermission(AgentSourcesSchemas, "workspace.sources.manage") }, feature: "agent.metadata" },
  // Training — require workspace.agents.manage
  { surface: "agent.training.index", route: { path: "training", element: withPermission(AgentTraining, "workspace.agents.manage") } },
  // Schedules — release-gated (legacy agent:schedules -> agent.schedules)
  { surface: "agent.training.schedules", route: { path: "training/schedules", element: withPermission(AgentSchedules, "workspace.agents.manage") }, feature: "agent.schedules" },
  { surface: "agent.training.job-history", route: { path: "training/job-history", element: withPermission(AgentJobHistory, "workspace.agents.manage") } },
  // Analytics — view-level access
  { surface: "agent.analytics", route: { path: "analytics", element: withSuspense(AgentAnalytics) } },
  // Conversation Insights — release-gated (agent.insights); chatbot-only (the
  // sidebar tab is additionally gated on isRagChatbot).
  { surface: "agent.insights", route: { path: "insights", element: withSuspense(AgentInsights) }, feature: "agent.insights" },
  ], surfaces);
}

// ---------------------------------------------------------------------------
// Tenant route definitions
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Workspace-level routes (rendered inside TenantLayout with workspace sidebar)
// ---------------------------------------------------------------------------

export function buildWorkspaceRoutes(surfaces: RouteSurfaceFilter): RouteObject[] {
  return gateRoutes([
  // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: a REDIRECT (the permanently-on category this rule's own message names); the same pre-existing redirect route, only its element changed from a literal <Navigate> to LandingRedirect (SPEC-TENANT-SURFACES-RULE-005(b)); every target it resolves to keeps its own gate.
  { surface: NO_REGISTRY_ROW, route: { index: true, element: <LandingRedirect scope="workspace" group="index" /> } },
  { surface: "tenant.applications", feature: "tenant.applications", route: { path: "applications", element: withPermission(ApplicationsPage, "lowcode.apps.view") } },
  { surface: "tenant.agents", route: { path: "agents", element: <AgentsChooserRoute>{withSuspense(AgentsPage)}</AgentsChooserRoute> } },
  // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: a REDIRECT (the permanently-on category this rule's own message names); the same pre-existing redirect route, only its element changed from a literal <Navigate> to LandingRedirect (SPEC-TENANT-SURFACES-RULE-005(b)); every target it resolves to keeps its own gate.
  { surface: "tenant.sources", route: { path: "sources", element: <LandingRedirect scope="workspace" group="sources" /> } },
  // Tenant sources — release-gated (legacy tenant:sources/files, tenant:sources/website)
  { surface: "tenant.sources.files", route: { path: "sources/files", element: withSuspense(SourcesPage) }, feature: "tenant.sources.files" },
  { surface: "tenant.sources.website", route: { path: "sources/website", element: withSuspense(SourcesPage) }, feature: "tenant.sources.website" },
  { surface: "tenant.usage", route: { path: "usage", element: withSuspense(UsagePage) } },
  { surface: "tenant.features", route: { path: "features", element: withSuspense(FeaturesPage) } },
  // USER-level, no ProtectedRoute (D-TSG-02). Its registry row is
  // configurable: false, so the surface filter above keeps it for every caller
  // - including on the me/surfaces error path, where only pinned keys survive.
  { surface: "tenant.my-settings", route: { path: "my-settings", element: withSuspense(MySettingsPage) } },
  // nosemgrep: feature-toggle-route-untoggled  // toggle-ok: a REDIRECT (the permanently-on category this rule's own message names); the same pre-existing redirect route, only its element changed from a literal <Navigate> to LandingRedirect (SPEC-TENANT-SURFACES-RULE-005(b)); every target it resolves to keeps its own gate.
  { surface: "tenant.settings", route: { path: "settings", element: <LandingRedirect scope="workspace" group="settings" /> } },
  {
    surface: "tenant.settings.general",
    route: {
      path: "settings/general",
      element: (
        <ProtectedRoute requiredPermission="workspace.settings.manage">
          {withSuspense(SettingsGeneral)}
        </ProtectedRoute>
      ),
    },
  },
  {
    surface: "tenant.settings.members",
    route: {
      path: "settings/members",
      element: (
        <ProtectedRoute requiredPermission="workspace.members.manage">
          {withSuspense(SettingsMembers)}
        </ProtectedRoute>
      ),
    },
  },
  // Permissions — release-gated (legacy tenant:settings/permissions)
  {
    surface: "tenant.settings.permissions",
    route: {
      path: "settings/permissions",
      element: (
        <ProtectedRoute requiredPermission="workspace.roles.manage">
          {withSuspense(SettingsPermissions)}
        </ProtectedRoute>
      ),
    },
    feature: "tenant.settings.permissions",
  },
  {
    surface: "tenant.settings.permissions-management",
    route: {
      path: "settings/permissions-management",
      element: (
        <ProtectedRoute requiredPermission="workspace.roles.manage">
          {withSuspense(PermissionsManagementPage)}
        </ProtectedRoute>
      ),
    },
    feature: "tenant.settings.permissions",
  },
  {
    surface: "tenant.settings.roles",
    route: {
      path: "settings/roles",
      element: (
        <ProtectedRoute requiredPermission="workspace.roles.manage">
          {withSuspense(SettingsRoles)}
        </ProtectedRoute>
      ),
    },
  },
  {
    surface: "tenant.settings.plugins",
    route: {
      path: "settings/plugins",
      element: (
        <ProtectedRoute requiredPermission="workspace.plugins.manage">
          {withSuspense(SettingsPlugins)}
        </ProtectedRoute>
      ),
    },
    feature: "tenant.settings.plugins",
  },
  {
    // The tenant's AI answer records (SPEC-FRONTEND-UI-058; SPEC-EU-AI-ACT
    // API-009). workspace.settings.manage is the frontend string of
    // settings.manage, the General and Storage precedent.
    surface: "tenant.settings.answer-provenance",
    route: {
      path: "settings/answer-provenance",
      element: (
        <ProtectedRoute requiredPermission="workspace.settings.manage">
          {withSuspense(SettingsAnswerProvenance)}
        </ProtectedRoute>
      ),
    },
    feature: "tenant.settings.answer-provenance",
  },
  {
    surface: "tenant.settings.schemas",
    route: {
      path: "settings/schemas",
      element: (
        <ProtectedRoute requiredPermission="workspace.settings.manage">
          {withSuspense(SettingsSchemas)}
        </ProtectedRoute>
      ),
    },
    feature: "tenant.settings.schemas",
  },
  // Genuine not-found terminal for truly-unknown workspace-level paths. Ranked
  // ahead of the SPA-level redirect catch-all in App.tsx (declared earlier).
  { surface: NOT_A_SURFACE, route: { path: "*", element: <NotFoundPage homePath="/" /> } },
  ], surfaces);
}

// ---------------------------------------------------------------------------
// Agent detail route (rendered inside AgentLayout — no workspace sidebar)
// ---------------------------------------------------------------------------
//
// LOAD-BEARING: this `path:"*"` is the agent-detail SHELL (AgentDetailPage +
// nested agentDetailChildren), NOT a catch-all redirect. Preserved verbatim.
// The shell itself is not surface-filtered: `agent.shell` is configurable:false
// (RULE-005(a)), and it must stay registered so an omitted child lands on the
// shell's own `*` not-found child (RULE-005(d)) instead of leaving the shell.

export function buildAgentDetailRoute(surfaces: RouteSurfaceFilter): RouteObject {
  return {
    index: false,
    path: "*",
    handle: { surface: "agent.shell" },
    element: (
      <RouteErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <AgentDetailPage />
        </Suspense>
      </RouteErrorBoundary>
    ),
    children: [
      ...buildAgentDetailChildren(surfaces),
      // An omitted or unknown agent sub-path renders the not-found page INSIDE
      // the shell rather than the shell with an empty content pane
      // (SPEC-TENANT-SURFACES-RULE-005(d), SPEC-FEATURE-TOGGLES-RULE-005).
      { path: "*", handle: { surface: NOT_A_SURFACE }, element: <NotFoundPage homePath="/agents" /> },
    ],
  };
}
